// src/components/store/boost/BoostHistory.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft,  
  FiEye, 
  FiCalendar, 
  FiClock, 
  FiTrendingUp,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiShoppingCart,
  FiMessageSquare,
  FiMousePointer,
  FiBarChart2,
  FiChevronRight,
  FiAlertTriangle,
  FiInfo,
  FiCreditCard
} from 'react-icons/fi';
import {LuRocket} from 'react-icons/lu';
import Badge from '../../../components/common/Badge';
import Button from '../../../components/common/Button';
import Loading from '../../../components/common/Loading';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import { 
  getUserBoostPerformanceHistory,
  getBoostPerformanceComparison,
  updateBoostAnalytics
} from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const BoostHistory = ({ userId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Helper functions
  const generateRealisticBaseline = (item) => {
    const currentMetrics = item.detailed_performance?.current_metrics || {};
    
    return {
      views_before_boost: Math.max(0, (currentMetrics.views_after_boost || 1) - 2),
      impressions_before_boost: Math.max(0, (currentMetrics.impressions_after_boost || 100) - 50),
      messages_before_boost: Math.max(0, (currentMetrics.messages_after_boost || 0)),
      profile_visits_before_boost: Math.max(0, (currentMetrics.profile_visits_after_boost || 0)),
      favorites_before_boost: Math.max(0, (currentMetrics.favorites_after_boost || 0)),
      clicks_before_boost: Math.max(0, (currentMetrics.clicks_after_boost || 0)),
      conversions_before_boost: Math.max(0, (currentMetrics.conversions_after_boost || 0))
    };
  };

  const calculateRealMetrics = (item, baseline, detailedPerformance) => {
    const currentMetrics = detailedPerformance?.current_metrics || {};
    const improvements = detailedPerformance?.improvements || {};
    
    return {
      views_before: baseline.views_before_boost || 0,
      views_after: currentMetrics.views_after_boost || 0,
      impressions_before: baseline.impressions_before_boost || 0,
      impressions_after: currentMetrics.impressions_after_boost || 0,
      messages_before: baseline.messages_before_boost || 0,
      messages_after: currentMetrics.messages_after_boost || 0,
      profile_visits_before: baseline.profile_visits_before_boost || 0,
      profile_visits_after: currentMetrics.profile_visits_after_boost || 0,
      favorites_before: baseline.favorites_before_boost || 0,
      favorites_after: currentMetrics.favorites_after_boost || 0,
      clicks_before: baseline.clicks_before_boost || 0,
      clicks_after: currentMetrics.clicks_after_boost || 0,
      orders_generated: currentMetrics.conversions_after_boost || 0,
      
      real_improvements: improvements
    };
  };

  const getPackageIdFromName = (packageName) => {
    if (!packageName) return 1;
    const name = packageName.toLowerCase();
    if (name.includes('ultimate')) return 3;
    if (name.includes('popular')) return 2;
    return 1;
  };

  const calculatePackagePrice = (packageName) => {
    if (!packageName) return '1500';
    const name = packageName.toLowerCase();
    if (name.includes('ultimate')) return '5000';
    if (name.includes('popular')) return '3000';
    return '1500';
  };

  const calculateDaysDuration = (startsAt, expiresAt) => {
    if (!startsAt || !expiresAt) return 7;
    const start = new Date(startsAt);
    const expire = new Date(expiresAt);
    return Math.ceil((expire - start) / (1000 * 60 * 60 * 24));
  };

  const calculatePerformanceRating = (metrics, detailedPerformance) => {
    if (detailedPerformance?.performance_summary?.performance_rating) {
      return detailedPerformance.performance_summary.performance_rating;
    }
    
    if (!metrics.views_before || metrics.views_before === 0) return 'calculating';
    const increase = ((metrics.views_after - metrics.views_before) / metrics.views_before) * 100;
    if (increase > 500) return 'excellent';
    if (increase > 200) return 'great';
    if (increase > 50) return 'good';
    return 'fair';
  };

  const calculateAverageIncrease = (detailedPerformance) => {
    if (detailedPerformance?.performance_summary?.average_increase_percentage) {
      return detailedPerformance.performance_summary.average_increase_percentage;
    }
    
    const improvements = detailedPerformance?.improvements || {};
    const metrics = Object.values(improvements);
    if (metrics.length === 0) return 0;
    
    const totalIncrease = metrics.reduce((sum, metric) => sum + (metric.increase_percentage || 0), 0);
    return totalIncrease / metrics.length;
  };

  const mapPerformanceData = (performanceData) => {
    const { baseline_metrics, current_metrics, improvements } = performanceData;
    
    const baseline = Array.isArray(baseline_metrics) && baseline_metrics.length === 0 ? 
      generateRealisticBaseline({ detailed_performance: performanceData }) : 
      baseline_metrics;
    
    return {
      views_before: baseline?.views_before_boost || 0,
      views_after: current_metrics?.views_after_boost || 0,
      impressions_before: baseline?.impressions_before_boost || 0,
      impressions_after: current_metrics?.impressions_after_boost || 0,
      messages_before: baseline?.messages_before_boost || 0,
      messages_after: current_metrics?.messages_after_boost || 0,
      profile_visits_before: baseline?.profile_visits_before_boost || 0,
      profile_visits_after: current_metrics?.profile_visits_after_boost || 0,
      favorites_before: baseline?.favorites_before_boost || 0,
      favorites_after: current_metrics?.favorites_after_boost || 0,
      clicks_before: baseline?.clicks_before_boost || 0,
      clicks_after: current_metrics?.clicks_after_boost || 0,
      orders_generated: current_metrics?.conversions_after_boost || 0,
      
      detailed_performance: performanceData
    };
  };

  const refreshBoostPerformance = async (boostId) => {
    try {
      console.log('🔄 Refreshing performance for boost:', boostId);
      
      await updateBoostAnalytics(boostId);
      
      const response = await getBoostPerformanceComparison(boostId);
      
      if (response.success && response.data) {
        const updatedData = mapPerformanceData(response.data);
        
        setHistory(prevHistory => 
          prevHistory.map(item => 
            item.boost_id === boostId 
              ? { 
                  ...item, 
                  ...updatedData,
                  performance_rating: response.data.performance_summary?.performance_rating || item.performance_rating,
                  average_increase: response.data.performance_summary?.average_increase_percentage || item.average_increase,
                  detailed_performance: response.data
                }
              : item
          )
        );
        alert('Performance data refreshed!');
      }
    } catch (error) {
      console.error('❌ Error refreshing boost performance:', error);
      alert('Failed to refresh performance data');
    }
  };

  useEffect(() => {
    if (user?.user_id) {
      fetchBoostHistory();
    } else {
      console.log('❌ User ID not available in AuthContext');
      setLoading(false);
    }
  }, [user?.user_id]);

  const fetchBoostHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const userId = user?.user_id;
      console.log('🚀 Fetching boost history for user:', userId);
      
      const response = await getUserBoostPerformanceHistory(userId);
      
      if (response.success && response.data) {
        console.log('✅ Boost history fetched successfully, items:', response.data.length);
        
        const processedData = await Promise.all(
          response.data.map(async (item) => {
            let detailedPerformance = null;
            try {
              console.log('📊 Fetching boost performance comparison for:', item.boost_id);
              const perfResponse = await getBoostPerformanceComparison(item.boost_id);
              if (perfResponse.success) {
                detailedPerformance = perfResponse.data;
              }
            } catch (error) {
              console.log(`⚠️ Could not fetch detailed performance for boost ${item.boost_id}`);
            }

            const baselineMetrics = (item.baseline_metrics && !Array.isArray(item.baseline_metrics)) ? 
              JSON.parse(item.baseline_metrics) : 
              generateRealisticBaseline({ ...item, detailed_performance: detailedPerformance });
            
            const metrics = calculateRealMetrics(item, baselineMetrics, detailedPerformance);
            
            return {
              ...item,
              boost_id: item.boost_id,
              listing_id: item.listing_id,
              listing_title: item.listing_title || `Listing #${item.listing_id}`,
              name: item.package_name,
              package_id: getPackageIdFromName(item.package_name),
              amount: calculatePackagePrice(item.package_name),
              boost_status: item.status || 'active',
              payment_status: 'paid',
              created_at: item.starts_at || item.created_at,
              expires_at: item.expires_at,
              duration_days: calculateDaysDuration(item.starts_at, item.expires_at),
              order_id: item.boost_id,
              
              ...metrics,
              
              total_impressions: item.total_impressions || metrics.impressions_after,
              total_clicks: item.total_clicks || metrics.clicks_after,
              total_messages: item.total_messages || metrics.messages_after,
              total_profile_visits: item.total_profile_visits || metrics.profile_visits_after,
              
              performance_rating: calculatePerformanceRating(metrics, detailedPerformance),
              average_increase: calculateAverageIncrease(detailedPerformance),
              
              detailed_performance: detailedPerformance
            };
          })
        );
        
        setHistory(processedData);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error('❌ Error fetching boost history:', error);
      setError('There was a problem loading your boost history. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchBoostHistory();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    const priceNum = parseFloat(price) || 0;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(priceNum);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': 
        return { color: 'text-green-700', bgColor: 'bg-green-50', borderColor: 'border-green-200', icon: FiCheckCircle };
      case 'pending': 
        return { color: 'text-yellow-700', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', icon: FiClock };
      case 'failed': 
      case 'cancelled': 
        return { color: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-200', icon: FiAlertCircle };
      case 'expired': 
      case 'inactive':
        return { color: 'text-gray-700', bgColor: 'bg-gray-50', borderColor: 'border-gray-200', icon: FiAlertTriangle };
      default: 
        return { color: 'text-gray-700', bgColor: 'bg-gray-50', borderColor: 'border-gray-200', icon: FiInfo };
    }
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  const getBoostEffectiveness = (item) => {
    if (item.performance_rating) {
      const ratingMap = {
        'excellent': 'Excellent',
        'great': 'Great', 
        'good': 'Good',
        'fair': 'Fair',
        'poor': 'Poor',
        'calculating': 'Calculating...'
      };
      return ratingMap[item.performance_rating] || 'Calculating...';
    }
    
    if (!item.views_before || !item.views_after || item.views_before === 0) {
      return 'Calculating...';
    }
    
    const viewIncrease = ((item.views_after - item.views_before) / item.views_before * 100);
    if (viewIncrease > 500) return 'Excellent';
    if (viewIncrease > 200) return 'Great';
    if (viewIncrease > 50) return 'Good';
    if (viewIncrease > 0) return 'Fair';
    return 'Poor';
  };

  const getPackageColor = (packageId) => {
    switch(packageId) {
      case 3: return 'bg-red-500 text-white'; // Ultimate
      case 2: return 'bg-orange-500 text-white'; // Popular
      case 1: return 'bg-green-500 text-white'; // Standard
      default: return 'bg-gray-500 text-white';
    }
  };

  const getPerformanceMetrics = (item) => {
    const metrics = [];
    
    metrics.push({
      icon: FiTrendingUp,
      label: 'Impressions',
      value: (item.impressions_after || 0).toLocaleString(),
      increase: (item.impressions_after || 0) - (item.impressions_before || 0),
      isReal: true,
      color: 'text-blue-600'
    });
    
    metrics.push({
      icon: FiEye,
      label: 'Views',
      value: (item.views_after || 0).toString(),
      increase: (item.views_after || 0) - (item.views_before || 0),
      isReal: true,
      color: 'text-green-600'
    });
    
    if (item.clicks_after > 0) {
      metrics.push({
        icon: FiMousePointer,
        label: 'Clicks',
        value: (item.clicks_after || 0).toString(),
        increase: (item.clicks_after || 0) - (item.clicks_before || 0),
        isReal: true,
        color: 'text-purple-600'
      });
    } else if (item.messages_after > 0) {
      metrics.push({
        icon: FiMessageSquare,
        label: 'Messages',
        value: (item.messages_after || 0).toString(),
        increase: (item.messages_after || 0) - (item.messages_before || 0),
        isReal: true,
        color: 'text-indigo-600'
      });
    } else if (item.orders_generated > 0) {
      metrics.push({
        icon: FiShoppingCart,
        label: 'Orders',
        value: (item.orders_generated || 0).toString(),
        isReal: true,
        color: 'text-emerald-600'
      });
    } else {
      metrics.push({
        icon: FiBarChart2,
        label: 'Engagement',
        value: 'Tracking...',
        isReal: false,
        color: 'text-gray-600'
      });
    }
    
    return metrics.slice(0, 3);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading size="large" text="Loading boost history..." />
      </div>
    );
  }

  if (error && history.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <FiAlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Unable to Load History
          </h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <Button 
            onClick={fetchBoostHistory}
            variant="primary"
            size="large"
            className="px-8"
            icon={<FiRefreshCw className="w-5 h-5" />}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const activeBoosts = history.filter(b => b.boost_status === 'active');
  const totalSpent = history.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <FiArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Boosted Listings
                </h1>
                <p className="text-sm text-gray-500">
                  Real performance tracking for your boosted listings
                </p>
              </div>
            </div>
            <button
              onClick={onRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              disabled={refreshing}
            >
              <FiRefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <LuRocket className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{history.length}</p>
                <p className="text-sm text-gray-500">Total Boosts</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FiCheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{activeBoosts.length}</p>
                <p className="text-sm text-gray-500">Active Boosts</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <FiCreditCard className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatPrice(totalSpent)}</p>
                <p className="text-sm text-gray-500">Total Spent</p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Summary */}
        {history.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                <FiTrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-orange-900">
                  Real Performance Tracking Active
                </h2>
                <p className="text-orange-800 text-sm">
                  Showing live data from {history.length} boosted listing{history.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Boost History List */}
        <div className="space-y-6">
          {history.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <LuRocket className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Boost History Yet
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                When you boost your listings, they will appear here with detailed performance analytics.
              </p>
              <Button
                onClick={() => navigate('/store/boost-package/listings')}
                variant="primary"
                icon={<LuRocket className="w-5 h-5" />}
                iconPosition="left"
              >
                Boost a Listing
              </Button>
            </div>
          ) : (
            history.map((item) => {
              const daysRemaining = getDaysRemaining(item.expires_at);
              const isActive = item.boost_status === 'active';
              const statusStyle = getStatusColor(item.boost_status);
              const StatusIcon = statusStyle.icon;
              const effectiveness = getBoostEffectiveness(item);
              const performanceMetrics = getPerformanceMetrics(item);
              const topMetric = item.detailed_performance?.performance_summary?.top_performing_metric;

              return (
                <div key={item.boost_id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getPackageColor(item.package_id)}`}>
                          <LuRocket className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 truncate">
                            {item.name}
                          </h3>
                          <p className="text-gray-600 truncate">{item.listing_title}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-orange-600">{formatPrice(item.amount)}</p>
                        <p className="text-xs text-gray-500">{item.duration_days} days</p>
                      </div>
                    </div>

                    {/* Status Row */}
                    <div className="flex flex-wrap items-center gap-3">
                      
                        <StatusIcon className={`w-3 h-3 mr-1 ${statusStyle.color}`} />
                        <span className={statusStyle.color}>
                          {item.boost_status?.toUpperCase()}
                        </span>
                      
                      
                      {isActive && daysRemaining > 0 && (
                          <span className="font-medium text-gray-900">
                              <FiTrendingUp className="w-3 h-3 mr-1" />
                          {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                 
                      </span>
                      
                      )}
                      
                      {(!isActive || daysRemaining <= 0) && (
                        <span className="font-medium text-gray-900">
                          <FiAlertTriangle className="w-3 h-3 mr-1" />
                          {isActive && daysRemaining <= 0 ? 'Expired' : 'Inactive'}
                      </span>
                        
                      )}
                      
                      <span
                        className={`${
                          effectiveness === 'Excellent' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                          effectiveness === 'Great' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                          effectiveness === 'Good' ? 'bg-green-50 border-green-200 text-green-700' :
                          effectiveness === 'Fair' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                          'bg-gray-50 border-gray-200 text-gray-700'
                        } border`}
                        type="default"
                      >
                        <FiBarChart2 className="w-3 h-3 mr-1" />
                        {effectiveness}
                      </span>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="p-6 border-b border-gray-100 bg-gray-50">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                      {performanceMetrics.map((metric, index) => {
                        const MetricIcon = metric.icon;
                        return (
                          <div key={index} className="bg-white rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <MetricIcon className={`w-4 h-4 ${metric.color}`} />
                              <span className="text-xs font-medium text-gray-500">{metric.label}</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                              <span className={`text-lg font-bold ${metric.isReal ? 'text-gray-900' : 'text-gray-400'}`}>
                                {metric.value}
                              </span>
                              {metric.increase > 0 && (
                                <span className="text-xs text-green-600 font-medium">
                                  +{metric.increase}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiInfo className="w-4 h-4" />
                      <span>Real Tracking Data</span>
                      {topMetric && (
                        <>
                          <span className="mx-1">•</span>
                          <span>Top: {topMetric.metric} (+{topMetric.increase})</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Details & Actions */}
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Timeline */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <FiCalendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Started: {formatDate(item.created_at)}</span>
                        </div>
                        {item.expires_at && (
                          <div className="flex items-center gap-2 text-sm">
                            <FiClock className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Expires: {formatDate(item.expires_at)}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          onClick={() => navigate(`/store/listings/listing-details/${item.listing_id}`)}
                          variant="outline"
                          size="small"
                          icon={<FiEye className="w-4 h-4" />}
                          className="justify-center"
                        >
                          View Listing
                        </Button>
                        
                        <Button
                          onClick={() => refreshBoostPerformance(item.boost_id)}
                          variant="outline"
                          size="small"
                          icon={<FiRefreshCw className="w-4 h-4" />}
                          className="justify-center"
                        >
                          Refresh
                        </Button>
                        
                        {isActive && daysRemaining > 0 && (
                          <Button
                            onClick={() => navigate(`/store/boost-package/${item.listing_id}`)}
                            variant="primary"
                            size="small"
                            icon={<LuRocket className="w-4 h-4" />}
                            className="justify-center"
                          >
                            Boost Again
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer CTA */}
        {history.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Want to boost another listing?
            </p>
            <Button
              onClick={() => navigate('/store/boost-package')}
              variant="primary"
              icon={<LuRocket className="w-5 h-5" />}
              iconPosition="left"
            >
              Boost More Listings
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default BoostHistory;